import { renderToStaticMarkup } from "react-dom/server";
import type { Components } from "react-markdown";
import { validateFpAttributeValue } from "@forgepoint/shared";
import { MarkdownReact } from "@/components/MarkdownReact";
import { DEFAULT_MARKDOWN_CLASS } from "./MarkdownProse";
import {
  parseDocumentHtml,
  resolveBlock,
  serializeDocumentHtml,
  type DocumentEdit,
} from "./documentEditor";
import { remarkFieldTokens } from "./remarkFieldTokens";

/**
 * Markdown-to-document-HTML conversion for the quote-document editor.
 *
 * Authored (source-less) `<fp-markdown>` blocks are a legacy format: the
 * editor now normalizes them into plain HTML on load so every prose block
 * edits inline, the same way. The renderer (FpMarkdown) keeps supporting
 * stored legacy documents that were never re-saved.
 *
 * Conversion runs the SAME pipeline the renderer uses (MarkdownReact, i.e.
 * react-markdown + remark-gfm), so converted HTML matches what the markdown
 * rendered as - with two deliberate differences:
 * - `{{ source }}` tokens become live `<fp-field>` elements when the source is
 *   valid (so they keep resolving against the quote), and stay literal text
 *   when it is not (an invalid source would fail the backend sanitizer).
 * - Markdown constructs whose tags the document vocabulary forbids (links,
 *   code, strikethrough, images) are mapped onto allowed tags, since the
 *   backend write path rejects any disallowed tag.
 */

/**
 * Overrides for markdown constructs whose default tags are not in the
 * document vocabulary's tag allowlist.
 */
const VOCABULARY_SAFE_COMPONENTS: Components = {
  a: ({ children }) => <span className="underline">{children}</span>,
  code: ({ children }) => <span className="font-mono">{children}</span>,
  pre: ({ children }) => <div>{children}</div>,
  del: ({ children }) => <span className="line-through">{children}</span>,
  img: () => null,
  input: () => null,
};

const fieldTokenElements = remarkFieldTokens((source) =>
  validateFpAttributeValue("fp-field", "source", source, false).length === 0
    ? { fieldSource: source }
    : { value: `{{${source}}}` },
);

/**
 * Converts markdown to vocabulary-safe document HTML (no wrapper element).
 * @param markdown - The markdown text to convert
 * @returns The converted HTML fragment
 */
export function markdownToDocumentHtml(markdown: string): string {
  return renderToStaticMarkup(
    <MarkdownReact
      extraRemarkPlugins={[fieldTokenElements]}
      components={VOCABULARY_SAFE_COMPONENTS}
    >
      {markdown}
    </MarkdownReact>,
  );
}

/**
 * Replaces every authored (source-less) `<fp-markdown>` block in a stored
 * document with a plain-HTML prose `<div>` holding the converted markdown, so
 * all authored prose edits inline. Bound fp-markdown blocks and repeater
 * `<template>` interiors are untouched. Documents without authored blocks are
 * returned unchanged (same string), so unaffected documents never read dirty.
 * @param html - The stored document HTML
 * @returns The normalized document HTML
 */
export function normalizeAuthoredMarkdownBlocks(html: string): string {
  const doc = parseDocumentHtml(html);
  const authored = Array.from(
    doc.querySelectorAll("fp-markdown:not([source])"),
  );
  if (authored.length === 0) {
    return html;
  }
  authored.forEach((element) =>
    element.replaceWith(
      buildProseWrapper(doc, element, element.textContent ?? ""),
    ),
  );
  return serializeDocumentHtml(doc);
}

/**
 * Converts a BOUND fp-markdown block into plain, inline-editable HTML: the
 * given currently-resolved field value is converted and replaces the block as
 * a prose `<div>`. This detaches the block from the quote field (future quote
 * changes no longer flow through) in exchange for in-place editing.
 * @param html - The stored document HTML
 * @param nodeId - Positional node id of the bound fp-markdown block
 * @param resolvedMarkdown - The field's currently-resolved markdown value
 * @returns The edit with the block reselected, or null when invalid
 */
export function convertBoundMarkdownToHtml(
  html: string,
  nodeId: string,
  resolvedMarkdown: string,
): DocumentEdit | null {
  const doc = parseDocumentHtml(html);
  const block = resolveBlock(doc, nodeId);
  if (
    !block ||
    block.tagName.toLowerCase() !== "fp-markdown" ||
    block.getAttribute("source") === null
  ) {
    return null;
  }
  block.replaceWith(buildProseWrapper(doc, block, resolvedMarkdown));
  return { html: serializeDocumentHtml(doc), nodeId };
}

/**
 * Builds the prose `<div>` replacing an fp-markdown block: the original's
 * class (or the default markdown prose class, matching what MarkdownProse
 * rendered) and inline style carry over, and the converted HTML becomes its
 * children.
 * @param doc - The parsed source document
 * @param original - The fp-markdown element being replaced
 * @param markdown - The markdown text to convert
 * @returns The replacement prose div
 */
function buildProseWrapper(
  doc: Document,
  original: Element,
  markdown: string,
): HTMLElement {
  const wrapper = doc.createElement("div");
  wrapper.className = original.getAttribute("class") ?? DEFAULT_MARKDOWN_CLASS;
  const style = original.getAttribute("style");
  if (style) {
    wrapper.setAttribute("style", style);
  }
  wrapper.innerHTML = markdownToDocumentHtml(markdown);
  return wrapper;
}
